// Cross-device pinned-message sync.
//
// A user pins a message on one device; the pin must replicate to their other
// linked devices without the server learning *which* message was pinned.
// Pin/unpin events are encrypted control payloads in the standard
// `kaether:e2ee:v1:` outer envelope; the server only sees an opaque
// ciphertext and cannot rebind a pin or flip pin to unpin because
// `messageId`, `channelId`, and `action` all live in the *signed* plaintext.
//
// Wire format of the *plaintext that gets encrypted* (NOT the on-the-wire
// envelope, which is the standard `kaether:e2ee:v1:` form):
//
//   kaether:pin:v1:<base64url(JSON)>
//
// JSON shape:
//   { "schema": 1, "channelId": "ch", "messageId": "m",
//     "action": "pin" | "unpin", "ts": 1234567890 }
//
// Ported from kaether_for_ios/Stoat/E2EE/PinnedMessage.swift

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type PinAction = "pin" | "unpin";

export interface PinnedMessagePayload {
  readonly schema: 1;
  readonly channelId: string;
  readonly messageId: string;
  readonly action: PinAction;
  /** Unix seconds (advisory; the server cannot forge this — it lives in the
   *  signed plaintext). Clients use local `observedAt` for display ordering. */
  readonly ts: number;
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export class PinnedMessageCodecError extends Error {
  readonly kind: "notAPinEvent" | "malformed" | "unsupportedSchema";
  readonly schemaVersion?: number;

  constructor(
    kind: "notAPinEvent" | "malformed" | "unsupportedSchema",
    detail?: string,
    schemaVersion?: number,
  ) {
    super(kind === "malformed" ? `malformed: ${detail ?? ""}` :
          kind === "unsupportedSchema" ? `unsupportedSchema(${schemaVersion})` :
          "notAPinEvent");
    this.name = "PinnedMessageCodecError";
    this.kind = kind;
    this.schemaVersion = schemaVersion;
  }
}

// ---------------------------------------------------------------------------
// Codec
// ---------------------------------------------------------------------------

const PREFIX = "kaether:pin:v1:";
const CURRENT_SCHEMA = 1;

function base64UrlEncode(bytes: Uint8Array): string {
  // btoa produces standard base64; then we convert to base64url.
  let b64 = btoa(String.fromCharCode(...bytes));
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function base64UrlDecode(s: string): Uint8Array | null {
  // Convert base64url → standard base64 and restore padding.
  let std = s.replace(/-/g, "+").replace(/_/g, "/");
  const pad = (4 - (std.length % 4)) % 4;
  std += "=".repeat(pad);
  try {
    const binary = atob(std);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  } catch {
    return null;
  }
}

export const PinnedMessageCodec = {
  prefix: PREFIX,

  isPinEvent(plaintext: string): boolean {
    return plaintext.startsWith(PREFIX);
  },

  encode(payload: PinnedMessagePayload): string {
    const json = JSON.stringify(payload);
    const bytes = new TextEncoder().encode(json);
    return PREFIX + base64UrlEncode(bytes);
  },

  decode(plaintext: string): PinnedMessagePayload {
    if (!plaintext.startsWith(PREFIX)) {
      throw new PinnedMessageCodecError("notAPinEvent");
    }
    const b64 = plaintext.slice(PREFIX.length);
    const bytes = base64UrlDecode(b64);
    if (!bytes) {
      throw new PinnedMessageCodecError("malformed", "invalid base64url");
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(new TextDecoder().decode(bytes));
    } catch (e) {
      throw new PinnedMessageCodecError(
        "malformed",
        `json decode failed: ${e instanceof Error ? e.message : String(e)}`,
      );
    }
    if (typeof parsed !== "object" || parsed === null) {
      throw new PinnedMessageCodecError("malformed", "not an object");
    }
    const obj = parsed as Record<string, unknown>;
    if (obj["schema"] !== CURRENT_SCHEMA) {
      throw new PinnedMessageCodecError(
        "unsupportedSchema",
        undefined,
        typeof obj["schema"] === "number" ? obj["schema"] : -1,
      );
    }
    if (typeof obj["channelId"] !== "string" || obj["channelId"] === "") {
      throw new PinnedMessageCodecError("malformed", "empty channelId");
    }
    if (typeof obj["messageId"] !== "string" || obj["messageId"] === "") {
      throw new PinnedMessageCodecError("malformed", "empty messageId");
    }
    if (obj["action"] !== "pin" && obj["action"] !== "unpin") {
      throw new PinnedMessageCodecError(
        "malformed",
        `invalid action: ${String(obj["action"])}`,
      );
    }
    if (typeof obj["ts"] !== "number") {
      throw new PinnedMessageCodecError("malformed", "ts must be a number");
    }
    return {
      schema: 1,
      channelId: obj["channelId"] as string,
      messageId: obj["messageId"] as string,
      action: obj["action"] as PinAction,
      ts: obj["ts"] as number,
    };
  },

  /**
   * Convenience: returns `null` for non-pin events.
   * Throws `PinnedMessageCodecError` only for payloads that claimed to be
   * pin events (i.e. prefix matched) but were malformed.
   */
  tryDecode(plaintext: string): PinnedMessagePayload | null {
    if (!plaintext.startsWith(PREFIX)) return null;
    return PinnedMessageCodec.decode(plaintext);
  },
} as const;
